import IndicatorButton from 'components/indicator/IndicatorButton';
import config from 'config/appConfig';

import { useEffect, useState, type JSX } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useAtom } from 'jotai';
import { AcctState } from 'stores/atoms/acctState';
import { SignupOldCompanyInfoSection } from './components/SignupOldCompanyInfoSection';
import { SignupOldIntegrationSection } from './components/SignupOldIntegrationSection';
import { SignupOldMailSection } from './components/SignupOldMailSection';
import { SignupOldUserSection } from './components/SignupOldUserSection';
import { useCompanyImageState } from './hooks/useCompanyImageState';
import { useCompanyRegistrationOld } from './hooks/useCompanyRegistrationOld';
import { submit } from 'utils/app';

/**
 * アカウント登録カンパニー情報入力画面
 * 2025/05/09 トライアルプラン改修で新しい画面を追加したため利用されていない
 */

export type SignupOldCompanyFormStateType = {
  companyName: string;
  companyTel: string;
  zipCode: string;
  prefectureDiv: number;
  city: string;
  address: string;
  buildingName: string;
  reportMailSenderName: string;
  reportMailSenderAddress: string;
  reportMailReplyAddress: string;
  reportMailSignatureHtml: string;
  privacyPolicyUrl: string;
  copyright: string;
  apiKeyBenchmark: string;
  apiKeyGoogleAnalytics: string;
  userName: string;
};

export default function SignupOldCompany(): JSX.Element {
  /* ----------------------------------------------------------------------- */
  /* 変数定義 */
  /* ----------------------------------------------------------------------- */
  const { showBoundary } = useErrorBoundary();
  const navigate = useNavigate();

  const [acctState] = useAtom(AcctState);

  const { registerCompany } = useCompanyRegistrationOld();
  const {
    isOpen,
    setIsOpen,
    isReset,
    setIsReset,
    imageBlob,
    setImageBlob,
    openCropper,
    resetCropper,
  } = useCompanyImageState();

  const [processing, setProcessing] = useState(false);

  const {
    control,
    register,
    handleSubmit,
    setValue,
    setError,
    formState: { errors },
  } = useForm<SignupOldCompanyFormStateType>();

  /* ----------------------------------------------------------------------- */
  /* ライフサイクル関数定義 */
  /* ----------------------------------------------------------------------- */
  useEffect(() => {
    // フォームの初期化
    setValue('reportMailSenderAddress', config.ses.from.address);
  }, [setValue]);

  /* ----------------------------------------------------------------------- */
  /* プライベート関数定義 */
  /* ----------------------------------------------------------------------- */
  /**
   * 登録処理
   */
  const onSave = () =>
    submit(
      processing,
      setProcessing,
      handleSubmit(async (data: SignupOldCompanyFormStateType) => {
        try {
          const result = await registerCompany(data, imageBlob);
          if (result.duplicated) {
            setError('companyName', { type: 'custom', message: '同じ会社は登録できません。' });
            return;
          }
          navigate('/');
        } catch (e: unknown) {
          showBoundary(e);
        }
      })
    );

  /* ----------------------------------------------------------------------- */
  /* JSX */
  /* ----------------------------------------------------------------------- */
  return (
    <>
      <section className="section-header mt-5">
        <div className="container">
          <div className="d-md-flex d-flex-header">
            <div className="flex-shrink-0 flex-left">
              <h2 className="flex-title">アカウントの登録</h2>
            </div>
          </div>
        </div>
      </section>
      <section className="section-basic-setting">
        <div className="container">
          <div className="box-common">
            <form className="form-profile g-3">
              <SignupOldCompanyInfoSection
                register={register}
                errors={errors}
                imageCropper={{
                  isOpen,
                  setIsOpen,
                  isReset,
                  setIsReset,
                  imageBlob,
                  setImageBlob,
                  openCropper,
                  resetCropper,
                }}
              />

              <SignupOldMailSection register={register} errors={errors} control={control} />

              <SignupOldIntegrationSection register={register} errors={errors} />

              {/* アカウント未登録の場合はユーザー情報を入力する */}
              {acctState.user === null && (
                <SignupOldUserSection register={register} errors={errors} />
              )}

              <div className="form-group">
                <div className="row form-row flex-md-row justify-content-between">
                  <div className="form-footer">
                    <button
                      type="button"
                      className="btn btn-submit w-100  max-width-238"
                      onClick={onSave}
                      disabled={processing}
                    >
                      <IndicatorButton visible={processing} />
                      BranQをはじめる
                    </button>
                  </div>{' '}
                </div>
              </div>
            </form>
          </div>
        </div>
      </section>
    </>
  );
}
